import Constants from '@/Constants';
import { usePrevious } from '@/lib/hooks';
import { CardMode } from '@/models/CardMode';
import { ClassEnrollment } from '@/models/ClassEnrollment';
import { ProgramListStudentViewRequest } from '@/models/ProgramListStudentViewRequest';
import { ProgramPaginationData } from '@/models/ProgramPaginationData';
import { SlideConfig } from '@/models/SlideConfig';
import { ViewType } from '@/models/ViewType';
import { analyticsService, LMSEvents } from '@/services/AnalyticsService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { getProgramListStudentViewRequest, getProgramSearchedRequest } from '@/store/program/action';
import { ListProgramClassStudentView, ProgramClassStudentView, ProgramSearchedRequest, ProgramTypes } from '@/store/program/types';
import '@/styles/variables.scss';
import React, { useEffect } from 'react';
import MagicSliderDots from 'react-magic-slider-dots';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import ProgramCard from '../ProgramCard';

interface Props {
    mode: CardMode;
    slideConfiguration?: SlideConfig;
    viewType: ViewType;
    localesToFilter?: string;
}

interface ProgramSectionConfiguration {
    requestData: ProgramListStudentViewRequest | ProgramSearchedRequest;
    action: (data: ProgramListStudentViewRequest | ProgramSearchedRequest) => void;
}

const getEventNameByType = (type: string, categoryId: string) => {
    if (type.startsWith('listCategorized')) {
        return LMSEvents.ACCESS_PROGRAM_SECTION_IN_CATEGORY.replace('%n', categoryId);
    }
    switch (type) {
        case 'listLastPublishedStudentView':
            return LMSEvents.ACCESS_PROGRAM_SECTION_LAST_PUBLISHED;
        case 'listInProgressStudentView':
            return LMSEvents.ACCESS_PROGRAM_SECTION_IN_PROGRESS;
        case 'listCompletedStudentView':
            return LMSEvents.ACCESS_PROGRAM_SECTION_MY_COMPLETED;
        case 'listSearchResultStudentView':
            return LMSEvents.ACCESS_PROGRAM_SECTION_SEARCH_RESULT;
        case 'listFavoriteStudentView':
            return LMSEvents.ACCESS_PROGRAM_SECTION_MY_FAVORITES;
        case 'listTrainingsEnrolledStudentView':
            return LMSEvents.ACCESS_PROGRAM_SECTION_TRAINING;
        default:
            return LMSEvents.ACCESS_PROGRAM;
    }
};

const logEvent = (entityId: number, entityName: string, type: string, categoryId: string) => {
    analyticsService.logEvent(getEventNameByType(type, categoryId), {
        entityId,
        entityName,
        moduleId: Constants.LMS_MODULE_ID,
        entityType: Constants.entityTypes.PROGRAM,
    });

    analyticsService.logEvent(getEventNameByType('default', categoryId), {
        entityId,
        entityName,
        moduleId: Constants.LMS_MODULE_ID,
        entityType: Constants.entityTypes.PROGRAM,
    });
};

const ProgramSection: React.FC<Props> = (props: Props) => {
    const { mode, slideConfiguration, viewType, localesToFilter } = props;

    const SLIDES_TO_SHOW: number = slideConfiguration ? slideConfiguration.slidesToShow : 2;
    const SLIDES_TO_SCROLL: number = slideConfiguration ? slideConfiguration.slidesToScroll : 2;
    // Todo novo carrossel que surgir, deverá ser implementado o refresh dele no método updateCourseStudentViewRequest
    const programsViewTypes: ViewType[] = [
        'listEssentialStudentView',
        'listLastPublishedStudentView',
        'listInProgressStudentView',
        'listSearchResultStudentView',
        'listCompletedStudentView',
        'listFavoriteStudentView',
        'listTrainingsEnrolledStudentView',
    ];

    const { programsListTotal, programsList, personId, programsListSize, searchTerm, modules, favoriteProgramList } = useSelector(
        (state: ApplicationState) => {
            const listProgramClassStudentView: ListProgramClassStudentView = state.program.listData[viewType];
            return {
                identity: state.identity.data,
                programsListTotal: listProgramClassStudentView && listProgramClassStudentView.total,
                programsList: listProgramClassStudentView && listProgramClassStudentView.list,
                programsListSize: listProgramClassStudentView && listProgramClassStudentView.list.length,
                personId: state.identity.data.person.id,
                strings: state.locale.data,
                searchTerm: state.search.data.term,
                modules: state.appOrganization.data.modules,
                loading: state.program.loading,
                favoriteProgramList: courseService.getFavoriteEntityListAsIdsString(state.favorite.favoriteList, Constants.entityTypes.PROGRAM),
            };
        },
    );

    const { id: categoryIdParam } = useParams<{ id: string }>();

    const prevFavoriteProgramList = usePrevious(favoriteProgramList);

    const dispatch = useDispatch();
    const history = useHistory();

    const getProgramRequest = (pageOffset: number): void => {
        if (viewType.startsWith('listCategorized') || programsViewTypes.indexOf(viewType) >= 0) {
            const { action, requestData } = getProgramConfiguration(pageOffset);
            dispatch(action(requestData));
        }
    };

    const getProgramConfiguration = (pageOffset: number): ProgramSectionConfiguration => {
        const baseConfiguration: ProgramSectionConfiguration = {
            action: getProgramListStudentViewRequest,
            requestData: {
                onlyCourseProgram: false,
                includeCourses: false,
                moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
                personId: personId,
                pagination: {
                    offset: pageOffset,
                    limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                    doRequest: true,
                } as ProgramPaginationData,
            } as ProgramListStudentViewRequest,
        } as ProgramSectionConfiguration;

        if (viewType.startsWith('listCategorized')) {
            return {
                action: getProgramListStudentViewRequest,
                requestData: {
                    ...baseConfiguration.requestData,
                    pagination: {
                        ...baseConfiguration.requestData.pagination,
                        orderBy: 'p.views desc',
                        programRequestSuccessType: ProgramTypes.CATEGORIZED_SUCCESS,
                    },
                    moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
                    categoryId: parseInt(categoryIdParam),
                    excludeExpiredAndNotFinished: true,
                },
            } as ProgramSectionConfiguration;
        }

        switch (viewType) {
            case 'listLastPublishedStudentView':
                return {
                    ...baseConfiguration,
                    requestData: {
                        ...baseConfiguration.requestData,
                        excludeExpiredAndNotFinished: true,
                        pagination: {
                            ...baseConfiguration.requestData.pagination,
                            orderBy: 'ce.enrollDate desc',
                            programRequestSuccessType: ProgramTypes.LAST_PUBLISHED_SUCCESS,
                        },
                    },
                };
            case 'listInProgressStudentView':
                return {
                    ...baseConfiguration,
                    requestData: {
                        ...baseConfiguration.requestData,
                        classEnrollmentStatus: Constants.classEnrollmentStatus.NOT_COMPLETED,
                        excludeExpiredAndNotFinished: true,
                        section: 'IN_PROGRESS',
                        pagination: {
                            ...baseConfiguration.requestData.pagination,
                            orderBy: 'p.statusDate desc',
                            programRequestSuccessType: ProgramTypes.IN_PROGRESS_SUCCESS,
                        },
                    },
                };
            case 'listCompletedStudentView':
                return {
                    ...baseConfiguration,
                    requestData: {
                        ...baseConfiguration.requestData,
                        classEnrollmentStatus: Constants.classEnrollmentStatus.COMPLETED,
                        pagination: {
                            ...baseConfiguration.requestData.pagination,
                            orderBy: 'ce.completedDate desc',
                            programRequestSuccessType: ProgramTypes.COMPLETED_SUCCESS,
                        },
                    },
                };
            case 'listSearchResultStudentView':
                return {
                    action: getProgramSearchedRequest,
                    requestData: {
                        term: searchTerm,
                        moduleIds: courseService.getModuleIdsForRequest(modules, [Constants.LMS_MODULE_ID]),
                        pagination: {
                            offset: pageOffset,
                            limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                            programRequestSuccessType: ProgramTypes.SEARCHED_SUCCESS,
                        },
                        locales: localesToFilter,
                    },
                } as ProgramSectionConfiguration;
            case 'listEssentialStudentView':
                return {
                    ...baseConfiguration,
                    requestData: {
                        ...baseConfiguration.requestData,
                        section: 'MANDATORY',
                        pagination: {
                            ...baseConfiguration.requestData.pagination,
                            programRequestSuccessType: ProgramTypes.ESSENTIALS_SUCCESS,
                        },
                    },
                } as ProgramSectionConfiguration;
            case 'listFavoriteStudentView':
                let auxPaginationData = { ...baseConfiguration.requestData.pagination };
                if (prevFavoriteProgramList !== favoriteProgramList) {
                    auxPaginationData = { ...baseConfiguration.requestData.pagination, offset: 0 };
                }

                return {
                    ...baseConfiguration,
                    requestData: {
                        ...baseConfiguration.requestData,
                        programIds: favoriteProgramList,
                        pagination: {
                            ...auxPaginationData,
                            orderBy: 'ce.enrollDate desc',
                            programRequestSuccessType: ProgramTypes.FAVORITE_SUCCESS,
                            doRequest: favoriteProgramList.length > 0,
                        },
                    },
                };

            case 'listTrainingsEnrolledStudentView':
                return {
                    ...baseConfiguration,
                    requestData: {
                        ...baseConfiguration.requestData,
                        section: 'TRAINING',
                        pagination: {
                            ...baseConfiguration.requestData.pagination,
                            offset: pageOffset,
                            limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
                            programRequestSuccessType: ProgramTypes.TRAININGS_SUCCESS,
                        },
                    },
                };
        }
        return {} as ProgramSectionConfiguration;
    };

    useEffect(() => {
        getProgramRequest(0);
    }, []);

    useEffect(() => {
        if (viewType === 'listSearchResultStudentView') {
            getProgramRequest(0);
        }
    }, [localesToFilter]);

    const handleLoadMoreOnAfterChange = (currentSlide: number) => {
        if (programsListTotal <= SLIDES_TO_SCROLL || currentSlide === 0 || currentSlide < programsListSize - SLIDES_TO_SCROLL) {
            return;
        }

        const slideToLoadMore: number = Math.floor(programsListSize / 2);
        if (programsListSize > SLIDES_TO_SCROLL && currentSlide >= slideToLoadMore && programsListSize < programsListTotal) {
            getProgramRequest(programsList.length);
        }
    };

    const getCarouselDots = (dots: boolean) => {
        return <MagicSliderDots dotWidth={30} dots={dots} numDotsToShow={4} />;
    };

    const settings = {
        afterChange: (currentSlide: number) => {
            handleLoadMoreOnAfterChange(currentSlide);
        },
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: SLIDES_TO_SHOW,
        slidesToScroll: SLIDES_TO_SCROLL,
        appendDots: (dots: boolean) => {
            return getCarouselDots(dots);
        },
    };

    const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, classEnrollment: ClassEnrollment) => {
        event.preventDefault();

        if (mode !== 'view') {
            return;
        }

        const {
            id,
            programClass: { program },
        } = classEnrollment;

        logEvent(program.id, program.name, viewType, categoryIdParam);

        let locationState;

        switch (history.location.pathname) {
            case '/search-result':
                locationState = { from: 'search' };
                break;
            case '/mine/completed':
                locationState = { from: 'completed' };
                break;
            case '/trainings/':
                locationState = { from: 'trainings' };
                break;
        }

        history.push({
            pathname: `/${Constants.PROGRAMS_LMS_SCREEN_ROUTE.replace('%e', id.toString()).replace('%p', program.id.toString())}`,
            state: locationState,
        });
    };

    const isCardDisabled = (status: string, isExpired: boolean): boolean => Constants.classEnrollmentStatus.COMPLETED !== status && isExpired;

    return (
        <>
            {programsList && programsList.length > 0 && (
                <div className="carrosell-slide-container">
                    <Slider {...settings}>
                        {programsList.map((studentView: ProgramClassStudentView) => {
                            const { classEnrollment } = studentView;
                            const { programClass } = classEnrollment;
                            const { program } = studentView.classEnrollment.programClass;
                            const { status } = studentView.classEnrollment;
                            if (studentView.classEnrollment.programClass.program) {
                                const disabled = isCardDisabled(status, programClass.isExpired);
                                return (
                                    <div key={program.id}>
                                        <Link
                                            className="link-to-course"
                                            onClick={(e) => !disabled && handleOnClick(e, classEnrollment)}
                                            to={
                                                mode === 'view' && !disabled
                                                    ? `/lms/enrollments/${studentView.classEnrollment.id}/programs/${program.id}`
                                                    : '#'
                                            }
                                        >
                                            <ProgramCard
                                                classEnrollmentStatus={status}
                                                disabled={disabled}
                                                program={program}
                                                programClass={studentView.classEnrollment.programClass}
                                            />
                                        </Link>
                                    </div>
                                );
                            }
                        })}
                    </Slider>
                </div>
            )}
        </>
    );
};

export default ProgramSection;
